import React, { Fragment, useState, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { NavLink } from 'react-router-dom'
import { nanoid } from '@reduxjs/toolkit'
import cx from 'classnames'

import styles from './Sidebar.module.scss'

import DashboardIcon from 'Icons/dashboard.svg'
import BotsIcon from 'Icons/bots.svg'
import ContentIcon from 'Icons/content.svg'
import AppsIcon from 'Icons/apps.svg'
import TrainingIcon from 'Icons/training.svg'
import SettingsIcon from 'Icons/settings.svg'
import AutomationIcon from 'Icons/automation.svg'
import ArrowDownIcon from 'Icons/arrow-down.svg'
import ArrowRightIcon from 'Icons/arrow-right.svg'

import Brand from './Brand'
import Product from './Product'
import { $getIsInternalFeatureSupported } from 'Selectors/account'

const DefaultIcon = () => null

const MenuIcons = {
  dashboard: DashboardIcon,
  bots: BotsIcon,
  content: ContentIcon,
  apps: AppsIcon,
  training: TrainingIcon,
  settings: SettingsIcon,
  automation: AutomationIcon,
  fallback: DefaultIcon
}

const prepareMenu = (menus, parentPath = '', isSubmenu = false) => {
  return menus.map((menu) => {
    const menuWihKey = {
      ...menu,
      path: `${parentPath}${(isSubmenu ? '/' : '') + menu.path}`,
      key: nanoid()
    }

    if (menu.submenus) {
      menuWihKey.submenus = prepareMenu(menu.submenus, menu.path, true)
    }

    return menuWihKey
  })
}

const getMenus = ({ isInternalFeatureSupported }) => {
  const contentMenuItem = {
    label: 'Content',
    path: 'content',
    icon: 'content',
    submenus: [
      {
        label: 'Answers',
        path: 'answers'
      },
      {
        label: 'Guides',
        path: 'guides'
      },
      {
        label: 'Knowledge Base',
        path: 'kbs'
      }
    ]
  }

  if (isInternalFeatureSupported) {
    contentMenuItem.submenus.push({
      label: 'Release Notes',
      path: 'release-notes'
    })
  }

  return prepareMenu([
    {
      label: 'Dashboard',
      path: 'dashboard',
      icon: 'dashboard'
    },
    {
      type: 'divider'
    },
    {
      label: 'Bots',
      path: 'bots',
      icon: 'bots'
    },
    // {
    //   label: 'Automation',
    //   path: '/automation',
    //   icon: 'automation'
    // },
    {
      type: 'divider'
    },
    contentMenuItem,
    {
      label: 'Training',
      path: 'training',
      icon: 'training'
    },
    {
      type: 'divider'
    },
    {
      label: 'Settings',
      path: 'settings',
      icon: 'settings'
    },
    {
      label: 'Apps',
      path: 'apps',
      icon: 'apps'
    }
  ])
}

const MenuItemContent = (props) => {
  const { menu, isActive, isSubMenu = false, isSubMenuOpen } = props
  const Icon = MenuIcons[menu.icon] || MenuIcons.fallback

  const showArrow = isActive && !isSubMenu
  const showDownArrow = showArrow && menu.submenus && isSubMenuOpen
  const showRightArrow = showArrow && !isSubMenuOpen

  return (
    <Fragment>
      <span className={styles.Icon}>
        <Icon width='1em' height='1em' />
      </span>
      <span className={styles.MenuLabel}>
        {menu.label}
        {showArrow && (
          <Fragment>
            {showDownArrow && (
              <span className={styles.ArrowContainer}>
                <ArrowDownIcon height='1em' width='1em' />
              </span>
            )}

            {showRightArrow && (
              <span className={styles.ArrowContainer}>
                <ArrowRightIcon height='1em' width='1em' />
              </span>
            )}
          </Fragment>
        )}
      </span>
    </Fragment>
  )
}

const MenuItem = (props) => {
  const { menu, isSubMenu = false } = props
  const [showSubMenu, setShowSubMenu] = useState(false)

  const handleMenuitemClick = () => {
    if (!menu.submenus) {
      return
    }

    setShowSubMenu(!showSubMenu)
  }

  return (
    <li className={styles.MenuItemContainer}>
      <NavLink
        onClick={handleMenuitemClick}
        to={menu.path}
        className={({ isActive }) => cx(styles.MenuItem, { [styles.Active]: isActive })}
      >
        {({ isActive }) => {
          return <MenuItemContent isActive={isActive} menu={menu} isSubMenu={isSubMenu} isSubMenuOpen={showSubMenu} />
        }}
      </NavLink>

      {menu.submenus && showSubMenu && (
        <ul className={cx(styles.SubMenu)}>
          {menu.submenus.map((submenu) => {
            return <MenuItem key={submenu.key} menu={submenu} isSubMenu />
          })}
        </ul>
      )}
    </li>
  )
}

const Sidebar = () => {
  const isInternalFeatureSupported = useSelector($getIsInternalFeatureSupported)
  const menus = useMemo(() => {
    return getMenus({ isInternalFeatureSupported })
  }, [isInternalFeatureSupported])
  return (
    <Fragment>
      <Brand />
      <ul className={styles.MenuContainer}>
        {menus.map((menu) => {
          if (menu.type === 'divider') {
            return <div key={menu.key} className={styles.Divider} />
          }

          return <MenuItem key={menu.key} menu={menu} />
        })}
      </ul>
      <Product />
    </Fragment>
  )
}

export default Sidebar
